<!-- src/components/course/ConferencesList.vue -->
<template>
	<div class="conferences-list">
		<div class="conferences-list__header" v-if="conferences.length > 0">
			<div>
				<p class="conferences-list__title">Конференции</p>
				<p class="conferences-list__subtitle">
					Уже созданные — их можно редактировать и удалять
				</p>
			</div>
			<button
				class="conferences-list__add-btn"
				type="button"
				@click="$emit('create-conference')"
			>
				Добавить конференцию
			</button>
		</div>

		<div v-if="loading" class="conferences-list__loading">
			<div class="spinner"></div>
			<p>Загрузка конференций...</p>
		</div>

		<div v-else-if="conferences.length === 0" class="conferences-list__empty">
			<p class="conferences-list__empty-text">Конференции еще не созданы</p>
			<button
				class="conferences-list__empty-btn"
				type="button"
				@click="$emit('create-conference')"
			>
				Создать первую конференцию
			</button>
		</div>

		<div v-else class="conferences-list__items created-list">
			<article
				v-for="conference in orderedConferences"
				:key="conference.id"
				class="created-card"
			>
				<div class="created-card__top">
					<div>
						<p class="created-card__title">
							{{ conference.title || 'Без названия' }}
						</p>
						<p class="created-card__datetime">
							{{ formatDate(conference.scheduled_at) }} ·
							{{ formatTime(conference.scheduled_at) }}
						</p>
					</div>
					<span class="created-card__status" :class="statusClass(conference)">
						{{ statusText(conference) }}
					</span>
				</div>

				<p v-if="conference.description" class="created-card__desc">
					{{ conference.description }}
				</p>

				<div class="created-card__meta">
					<!-- Завершённая конференция - показываем итоговую статистику -->
					<template v-if="conference.status === 'completed'">
						<span v-if="getTotalDuration(conference)">
							Длительность: {{ getTotalDuration(conference) }}
						</span>
						<span>
							Участников: {{ conference.participants_count || 0 }}
						</span>
					</template>
					<!-- Live конференция -->
          <template v-else-if="statusClass(conference) === 'live'">
            <span>Прошло: {{ getConferenceDuration(conference) || '00:00' }}</span>
          </template>
					<!-- Запланированная или прошедшая -->
					<template v-else>
						<span>
							Участники: {{ conference.participants_count || 0 }}
							<template v-if="conference.max_participants">
								/ {{ conference.max_participants }}
							</template>
						</span>
					</template>
					<a
						v-if="conference.meeting_link"
						:href="conference.meeting_link"
						target="_blank"
						rel="noopener"
						class="created-card__link"
					>
						Ссылка на конференцию
					</a>
				</div>

				<div class="created-card__actions">
					<button type="button" @click="$emit('edit-conference', conference)">
						Редактировать
					</button>
					<button
						type="button"
						class="danger"
						@click="$emit('delete-conference', conference)"
					>
						Удалить
					</button>
				</div>
			</article>
		</div>
	</div>
</template>

<script>
import { io } from 'socket.io-client'

let socket = null

export default {
	name: 'ConferencesList',

	props: {
		conferences: {
			type: Array,
			default: () => [],
		},
		loading: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			liveParticipants: {}, // { roomId: count }
			conferenceStartTimes: {}, // { conferenceId: started_at }
			currentTime: Date.now(),
			durationTimer: null,
		}
	},

	computed: {
		orderedConferences() {
			return [...this.conferences].sort(
				(a, b) =>
					new Date(b?.scheduled_at || 0).getTime() -
					new Date(a?.scheduled_at || 0).getTime()
			)
		},
	},

	watch: {
		conferences: {
			handler() {
				this.subscribeToLiveConferences()
			},
			immediate: true,
		},
	},

	mounted() {
		this.initSocket()
		
		// Таймер для обновления длительности
		this.durationTimer = setInterval(() => {
			this.currentTime = Date.now()
		}, 1000)
	},

	beforeUnmount() {
		this.disconnectSocket()
		
		if (this.durationTimer) {
			clearInterval(this.durationTimer)
			this.durationTimer = null
		}
	},

	methods: {
		initSocket() {
			if (socket) return

			// В production или на удалённом сервере используем текущий origin
			const wsUrl = (process.env.NODE_ENV === 'production' || window.location.hostname !== 'localhost')
				? window.location.origin
				: (import.meta.env.VITE_WS_URL || 'http://localhost:3003')
			
			socket = io(`${wsUrl}/webrtc`, {
				transports: ['websocket'],
				autoConnect: true,
			})

			socket.on('connect', () => {
				this.subscribeToLiveConferences()
			})

			socket.on('participant-count-updated', ({ roomId, count }) => {
				this.liveParticipants = {
					...this.liveParticipants,
					[roomId]: count,
				}
			})

			// Обработчик начала конференции
			socket.on('conference-started', ({ roomId, conferenceId, started_at }) => {
				// Используем conferenceId из события, или извлекаем из roomId
				let confId = conferenceId
				if (!confId) {
					const match = roomId.match(/conference_(\d+)/) || roomId.match(/^(\d+)$/)
					if (match) confId = match[1]
				}
				if (confId) {
					this.conferenceStartTimes = {
						...this.conferenceStartTimes,
						[String(confId)]: started_at,
					}
				}
			})
		},

		disconnectSocket() {
			if (socket) {
				socket.disconnect()
				socket = null
			}
		},

		subscribeToLiveConferences() {
			if (!socket?.connected) return

			// Подписываемся на все конференции для отслеживания участников
			this.conferences.forEach((conf) => {
				const roomId = conf.webrtc_channel || `conference_${conf.id}`
				socket.emit('subscribe-conference', { roomId })
			})
		},

		isLiveOrToday(conf) {
			const now = new Date()
			const start = new Date(conf?.scheduled_at || 0)
			const isToday = start.toDateString() === now.toDateString()
			const isLive = this.statusClass(conf) === 'live'
			return isLive || isToday
		},

		getLiveCount(conf) {
			const roomId = conf.webrtc_channel || `conference_${conf.id}`
			return this.liveParticipants[roomId] ?? null
		},

		formatDate(dateString) {
			if (!dateString) return ''

			const date = new Date(dateString)

			return date.toLocaleDateString('ru-RU', {
				day: '2-digit',
				month: 'long',
				year: 'numeric',
			})
		},

		formatTime(dateString) {
			if (!dateString) return ''

			const date = new Date(dateString)

			return date.toLocaleTimeString('ru-RU', {
				hour: '2-digit',
				minute: '2-digit',
			})
		},

		statusClass(conf) {
			// Если конференция завершена в БД - всегда past
			if (
				conf?.status === 'completed' ||
				conf?.status === 'ended' ||
				conf?.status === 'cancelled' ||
				conf?.ended_at
			)
				return 'past'

			const conferenceId = String(conf?.id)
			const startedAt =
				this.conferenceStartTimes[conferenceId] || conf?.started_at

			if (
				conf?.status === 'live' ||
				conf?.status === 'in_progress' ||
				startedAt
			)
				return 'live'

			const now = Date.now()
			const start = new Date(conf?.scheduled_at || 0).getTime()
			const end = start + (conf?.duration_minutes || 60) * 60 * 1000

			if (start > now) return 'upcoming'
			if (now <= end) return 'upcoming'
			return 'past'
		},

		statusText(conf) {
			// Если конференция завершена в БД
			if (conf?.status === 'completed') return 'Завершена'
			
			const map = {
				upcoming: 'Запланирована',
				live: 'В эфире',
				past: 'Прошла',
			}
			return map[this.statusClass(conf)]
		},

		/** Форматирование длительности в ЧЧ:ММ:СС */
		formatDuration(diffMs) {
			if (diffMs < 0) return null
			
			const totalSeconds = Math.floor(diffMs / 1000)
			const hours = Math.floor(totalSeconds / 3600)
			const minutes = Math.floor((totalSeconds % 3600) / 60)
			const seconds = totalSeconds % 60
			
			const pad = n => String(n).padStart(2, '0')
			
			if (hours > 0) {
				return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
			}
			return `${pad(minutes)}:${pad(seconds)}`
		},

		/** Получить текущую длительность live конференции */
		getConferenceDuration(conf) {
			// Используем currentTime для реактивности
			const _ = this.currentTime
			
			const conferenceId = String(conf.id)
			const startedAt = this.conferenceStartTimes[conferenceId] || conf.started_at
			
			if (!startedAt) return null
			
			const startTime = new Date(startedAt).getTime()
			const diffMs = Date.now() - startTime
			
			return this.formatDuration(diffMs)
		},

		/** Получить общую длительность завершённой конференции */
		getTotalDuration(conf) {
			if (!conf.started_at || !conf.ended_at) return null
			
			const startTime = new Date(conf.started_at).getTime()
			const endTime = new Date(conf.ended_at).getTime()
			const diffMs = endTime - startTime
			
			return this.formatDuration(diffMs)
		},
	},
}
</script>

<style scoped>
/* ============================================
   ОСНОВНЫЕ ПЕРЕМЕННЫЕ ШРИФТОВ
   ============================================ */
.conferences-list {
	--font-size-xl: 28px;      /* Заголовки */
	--font-size-lg: 22px;      /* Крупный текст */
	--font-size-base: 20px;    /* Основной текст */
	--font-size-md: 18px;      /* Средний текст */
	--font-size-sm: 17px;      /* Минимальный текст */
	
	width: 100%;
	padding: 24px;
}

/* ============================================
   HEADER
   ============================================ */
.conferences-list__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 32px;
	gap: 20px;
	flex-wrap: wrap;
}

.conferences-list__title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: var(--font-size-xl);
	font-weight: 600;
	margin: 0;
}

.conferences-list__subtitle {
	margin: 8px 0 0;
	color: #6b6b6b;
	font-family: 'Poppins', Helvetica;
	font-size: var(--font-size-sm);
	font-weight: 400;
}

.conferences-list__add-btn {
	display: flex;
	align-items: center;
	gap: 10px;
	background-color: #0096ed;
	color: #ffffff;
	border: none;
	border-radius: 10px;
	padding: 14px 28px;
	font-size: 17px;
	font-weight: 700;
	cursor: pointer;
	transition: all 0.3s ease;
}

.conferences-list__add-btn:hover {
	background-color: #0085d4;
	transform: translateY(-2px);
}

.conferences-list__add-icon {
	font-size: 24px;
	font-weight: 600;
	line-height: 1;
}

/* ============================================
   LOADING STATE
   ============================================ */
.conferences-list__loading {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 80px 20px;
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: var(--font-size-md);
}

.spinner {
	width: 50px;
	height: 50px;
	border: 4px solid #f3f3f3;
	border-top: 4px solid #0095ec;
	border-radius: 50%;
	animation: spin 1s linear infinite;
	margin-bottom: 20px;
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

/* ============================================
   EMPTY STATE
   ============================================ */
.conferences-list__empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 80px 20px;
	text-align: center;
}

.conferences-list__empty-text {
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: var(--font-size-lg);
	margin-bottom: 24px;
}

.conferences-list__empty-btn {
	background-color: #0096ed;
	color: #ffffff;
	border: none;
	border-radius: 10px;
	padding: 16px 32px;
	font-size: 17px;
	font-weight: 700;
	cursor: pointer;
	transition: all 0.3s ease;
}

.conferences-list__empty-btn:hover {
	background-color: #0085d4;
	transform: translateY(-2px);
}

/* ============================================
   CARDS GRID
   ============================================ */
.conferences-list__items {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
	gap: 24px;
}

/* ============================================
   CONFERENCE CARD
   ============================================ */
.created-card {
	border: 2px solid #e5e5e5;
	border-radius: 16px;
	padding: 24px;
	background: #fff;
	display: flex;
	flex-direction: column;
	gap: 16px;
	transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.created-card:hover {
	border-color: #0095ec;
	box-shadow: 0 6px 20px rgba(0, 149, 236, 0.18);
}

.created-card__top {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 16px;
}

.created-card__title {
	margin: 0;
	font-family: 'Poppins', Helvetica;
	font-size: var(--font-size-lg);
	font-weight: 600;
	color: #252641;
	line-height: 1.3;
}

.created-card__datetime {
	margin: 8px 0 0;
	color: #666;
	font-family: 'Poppins', Helvetica;
	font-size: var(--font-size-sm);
	font-weight: 400;
  white-space: nowrap;
}

.created-card__status {
	padding: 8px 16px;
	border-radius: 20px;
	font-family: 'Poppins', Helvetica;
	font-size: 15px;
	font-weight: 500;
	white-space: nowrap;
	height: fit-content;
	flex-shrink: 0;
}

.created-card__status.upcoming {
	background: #e8f4fd;
	color: #0b6fa4;
}

.created-card__status.live {
	background: #28a745;
	color: #fff;
	animation: pulse 2s infinite;
}

@keyframes pulse {
	0%, 100% {
		box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.4);
	}
	50% {
		box-shadow: 0 0 0 8px rgba(40, 167, 69, 0);
	}
}

.created-card__status.past {
	background: #f2f2f2;
	color: #666;
}

.created-card__desc {
	margin: 0;
	color: #444;
	font-family: 'Poppins', Helvetica;
	font-size: var(--font-size-md);
	line-height: 1.5;
}

.created-card__meta {
	display: flex;
	gap: 16px;
	align-items: center;
	flex-wrap: wrap;
	color: #555;
	font-family: 'Poppins', Helvetica;
	font-size: var(--font-size-sm);
	padding-top: 8px;
	border-top: 1px solid #f0f0f0;
}

.created-card__link {
	color: #0095ec;
	text-decoration: none;
	font-weight: 500;
	transition: color 0.2s ease;
}

.created-card__link:hover {
	color: #0077be;
	text-decoration: underline;
}

.duration-timer {
	color: #28a745;
	font-weight: 600;
	font-family: 'Consolas', 'Monaco', monospace;
}

/* ============================================
   CARD ACTIONS
   ============================================ */
.created-card__actions {
	display: flex;
	gap: 12px;
	margin-top: 8px;
}

.created-card__actions button {
	flex: 1;
	border: 2px solid #dcdcdc;
	border-radius: 10px;
	padding: 12px 16px;
	cursor: pointer;
	background: #f7f7f7;
	font-family: 'Poppins', Helvetica;
	font-size: var(--font-size-sm);
	font-weight: 500;
	color: #333;
	transition: all 0.2s ease;
}

.created-card__actions button:hover {
	background: #ececec;
	border-color: #0095ec;
	color: #0095ec;
}

.created-card__actions .danger {
	border-color: #e74c3c;
	color: #e74c3c;
	background: #fff5f3;
}

.created-card__actions .danger:hover {
	background: #ffe5e2;
	border-color: #c0392b;
	color: #c0392b;
}

/* ============================================
   RESPONSIVE DESIGN
   ============================================ */
@media (max-width: 768px) {
	.conferences-list {
		padding: 16px;
	}

	.conferences-list__header {
		flex-direction: column;
		align-items: flex-start;
	}

	.conferences-list__add-btn {
		width: 100%;
		justify-content: center;
	}

	.conferences-list__items {
		grid-template-columns: 1fr;
	}

	.created-card {
		padding: 20px;
	}

	.created-card__top {
		flex-direction: column;
	}

	.created-card__status {
		align-self: flex-start;
	}
}

@media (max-width: 480px) {
	.conferences-list {
		--font-size-xl: 24px;
		--font-size-lg: 20px;
		--font-size-base: 18px;
		--font-size-md: 17px;
		--font-size-sm: 16px;
	}

	.conferences-list__add-btn {
		padding: 12px 20px;
	}

	.created-card__actions {
		flex-direction: column;
	}
}
</style>
